/**
 * PILOT-25: TriageRow
 *
 * A single row in the Control Tower triage list.
 * Shows room, guest, category, age, assignee, and SLA breach state.
 * Clicking the row navigates to /requests/:id (PILOT-26 will build that page).
 */

import { useNavigate } from 'react-router-dom';
import type { TriageItem } from '../../services/controlTowerTypes';

const STATUS_LABEL: Record<TriageItem['status'], string> = {
  NEW: 'New',
  ESCALATED: 'Escalated',
  ASSIGNED: 'Assigned',
  IN_PROGRESS: 'In Progress',
};

const STATUS_BADGE: Record<TriageItem['status'], string> = {
  NEW: 'bg-sky-500/15 text-sky-300 border-sky-500/20',
  ESCALATED: 'bg-rose-500/15 text-rose-300 border-rose-500/20',
  ASSIGNED: 'bg-amber-500/15 text-amber-300 border-amber-500/20',
  IN_PROGRESS: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/20',
};

function formatAge(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

interface TriageRowProps {
  item: TriageItem;
}

export function TriageRow({ item }: TriageRowProps) {
  const navigate = useNavigate();

  return (
    <button
      type="button"
      onClick={() => navigate(`/requests/${item.id}`)}
      className={[
        'w-full flex items-center gap-3 px-4 py-3 text-left',
        'hover:bg-slate-700/50 transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-sky-500',
        item.slaBreached
          ? 'border-l-2 border-rose-500'
          : 'border-l-2 border-transparent',
      ].join(' ')}
      aria-label={`Room ${item.roomNumber} — ${item.guestName}, ${item.category}, ${formatAge(item.ageMinutes)} old`}
    >
      {/* Room number */}
      <span className="w-14 shrink-0 text-sm font-semibold text-slate-100">
        {item.roomNumber}
      </span>

      {/* Guest name */}
      <span className="w-32 shrink-0 text-sm text-slate-300 truncate">
        {item.guestName}
      </span>

      {/* Category */}
      <span className="w-32 shrink-0 text-sm text-slate-400 truncate">
        {item.category}
      </span>

      {/* Description */}
      <span className="flex-1 text-sm text-slate-500 truncate italic">
        {item.description ?? <span className="text-slate-600 not-italic">—</span>}
      </span>

      {/* Status badge */}
      <span
        className={`w-20 shrink-0 px-2 py-0.5 rounded text-[11px] font-medium border text-center ${STATUS_BADGE[item.status]}`}
      >
        {STATUS_LABEL[item.status]}
      </span>

      {/* Age */}
      <span
        className={`w-16 shrink-0 text-right text-sm tabular-nums ${
          item.slaBreached ? 'text-rose-400 font-semibold' : 'text-slate-400'
        }`}
      >
        {formatAge(item.ageMinutes)}
      </span>

      {/* Assignee */}
      <span
        className={`w-40 shrink-0 text-right text-sm truncate ${
          item.assignedTo ? 'text-slate-400' : 'text-rose-400 font-medium'
        }`}
      >
        {item.assignedTo ?? 'Unassigned'}
      </span>

      {/* Chevron */}
      <svg
        className="w-4 h-4 shrink-0 text-slate-600"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </button>
  );
}
